import React, { useRef, useMemo } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Points, PointMaterial } from "@react-three/drei";
import * as THREE from "three";

// Floating particle field
const Particles: React.FC<{ count?: number }> = ({ count = 1800 }) => {
  const ref = useRef<THREE.Points>(null!);

  const positions = useMemo(() => {
    const arr = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      arr[i * 3] = (Math.random() - 0.5) * 10;
      arr[i * 3 + 1] = (Math.random() - 0.5) * 5;
      arr[i * 3 + 2] = (Math.random() - 0.5) * 5;
    }
    return arr;
  }, [count]);

  useFrame(({ clock }) => {
    if (ref.current) {
      ref.current.rotation.y = clock.getElapsedTime() * 0.04;
      ref.current.rotation.x = Math.sin(clock.getElapsedTime() * 0.02) * 0.1;
    }
  });

  return (
    <Points ref={ref} positions={positions} stride={3} frustumCulled={false}>
      <PointMaterial
        transparent
        color="#91bd3a"
        size={0.025}
        sizeAttenuation
        depthWrite={false}
        opacity={0.7}
      />
    </Points>
  );
};

// Slowly drifting grid mesh lines
const GridLines: React.FC = () => {
  const ref = useRef<THREE.LineSegments>(null!);

  const geometry = useMemo(() => {
    const geo = new THREE.BufferGeometry();
    const verts: number[] = [];
    const step = 1;
    const range = 6;
    for (let i = -range; i <= range; i += step) {
      // horizontal lines
      verts.push(-range, i * 0.5, 0, range, i * 0.5, 0);
      // vertical lines
      verts.push(i, -range * 0.5, 0, i, range * 0.5, 0);
    }
    geo.setAttribute("position", new THREE.Float32BufferAttribute(verts, 3));
    return geo;
  }, []);

  useFrame(({ clock }) => {
    if (ref.current) {
      ref.current.rotation.z = clock.getElapsedTime() * 0.015;
      ref.current.position.z = -2;
    }
  });

  return (
    <lineSegments ref={ref} geometry={geometry}>
      <lineBasicMaterial color="#91bd3a" transparent opacity={0.07} />
    </lineSegments>
  );
};

interface ParticleHeroProps {
  height?: number | string;
}

const ParticleHero: React.FC<ParticleHeroProps> = ({ height = 420 }) => {
  return (
    <Canvas
      style={{ width: "100%", height }}
      camera={{ position: [0, 0, 4], fov: 60 }}
      gl={{ antialias: true, alpha: true }}
    >
      <ambientLight intensity={0.5} />
      <GridLines />
      <Particles />
    </Canvas>
  );
};

export default ParticleHero;
